<template>
  <div>
    <div
      v-if="$route.path !== '/showcase'"
      id="loadingLayer"
      :style="this.customBorderProp"
    >
      <transition name="fade">
        <div
          v-show="showCashier"
          :class="{ fixedPosition: $route.path !== '/demo' && $smallScreen }"
          :style="`${this.customBorderProp};overflow:initial`"
          id="cashier-container"
        >
          <div :style="this.customBorderProp" id="cashier-preview"></div>
        </div>
      </transition>
    </div>
    <transition name="fade">
      <div
        v-if="$route.path === '/showcase'"
        v-show="showCashier"
        :class="{
          'fixedPosition showcase': $route.path !== '/demo' && $smallScreen
        }"
        id="cashier-container"
      >
        <div id="cashier-preview"></div>
      </div>
    </transition>
  </div>
</template>

<script>
import _PaymentIQCashier from 'paymentiq-cashier-bootstrapper'
import { nextTick } from 'vue'
import { mapState, mapMutations } from 'vuex'
import {
  filterConfigForCashierVersion,
  getAttributesAsObject
} from '@/utils/helpers'

export default {
  name: 'Cashier',
  props: {
    setMaxHeight: { type: String, required: false, default: null },
    customConfigs: { type: Object, required: false, default: null },
    customFunction: { type: Function, required: false, default: null },
    customCssProp: { type: String, required: false, default: '' },
    customBorderProp: { type: String, required: false, default: '' }
  },
  data: () => ({
    cashier: null,
    showCashier: true,
    // True once cashierInitLoad fires. Prevents live-update paths from
    // posting to a half-initialised cashier that hasn't bootstrapped yet.
    cashierReady: false
  }),
  watch: {
    reloadCashier() {
      const cashierContainer = document.querySelector('#cashier-container')
      if (cashierContainer) {
        cashierContainer.innerHTML = '<div id="cashier-preview"></div>'
        this.loadCashierInstance()
      }
    },
    liveCssUpdate() {
      // Custom CSS edits are injected into the running cashier through the
      // live JS API (api.css) instead of tearing the whole widget down -
      // a full reload re-creates all iframes (incl. Hosted Fields) and
      // causes visible flicker.
      const customCss = (this.customCss && this.customCss[this.preset]) || ''
      if (this.cashierReady && this.cashier && typeof this.cashier.css === 'function') {
        this.cashier.css(this.composeCashierCss(customCss))
      } else {
        // Cashier not ready or live api unavailable - fall back to a full reload
        this.reloadCashierMutation()
      }
    },
    cashierMajorVersion(next, prev) {
      if (next !== prev) {
        const cashierContainer = document.querySelector('#cashier-container')
        if (cashierContainer) {
          cashierContainer.innerHTML = '<div id="cashier-preview"></div>'
          this.loadCashierInstance()
        }
      }
    }
  },
  computed: {
    ...mapState({
      reloadCashier: ({ config }) => config.reloadCashier,
      liveCssUpdate: ({ config }) => config.liveCssUpdate,
      config: ({ context }) => context.config,
      theme: ({ theme }) => theme.theme,
      customCss: ({ theme }) => theme.customCss,
      preset: ({ config }) => config.preset,
      attributes: ({ context }) => context.attributes,
      userAttributes: ({ context }) => context.userAttributes,
      cashierMajorVersion: ({ context }) => context.cashierMajorVersion
    }),
    fixedPosition() {
      return 'fixedPosition'
    }
  },
  methods: {
    ...mapMutations(['reloadCashierMutation']),
    composeCashierCss(customCss) {
      // Same CSS snapshot as passed to api.css() at init - reused for live
      // updates so both paths always inject the exact same styles.
      return `
        ${
          !this.customCssProp
            ? `#app-container {
          border-radius: .5rem!important;
        }`
            : this.customCssProp
        }
        ${customCss || ''}
      `
    },
    containerSizeToNumber(value) {
      try {
        let val = Number(value.split('px')[0])
        if (value.includes('vh')) {
          val = 700
        }
        return val
      } catch (err) {
        return 800
      }
    },
    getUserAndAttributes() {
      return getAttributesAsObject(this.attributes, this.userAttributes)
    },
    loadCashierInstance() {
      try {
        this.cashierReady = false
        window.CashierInstanceReady = false
        let configSettings = {}
        const { customCss, config, theme } = this.getConstantsAndCalculations()
        this.nextTickMethod(config)

        if (config.fetchConfig) {
          // Only basic configs if fetchConfig is true.
          configSettings = {
            environment: config.environment,
            merchantId: config.merchantId,
            userId: config.userId,
            sessionId: config.sessionId,
            method: config.method,
            fetchConfig: true
          }
        } else {
          configSettings = {
            ...filterConfigForCashierVersion(config, this.cashierMajorVersion),
            ...this.getUserAndAttributes(),
            theme: {
              ...theme
            }
          }
        }

        const PaymentIQCashier =
          window._PaymentIQCashier_FIXED_VERSION || _PaymentIQCashier

        this.cashier = new PaymentIQCashier(
          '#cashier-preview',
          {
            ...configSettings
          },
          (api) => {
            api.on({
              cashierInitLoad: async () => {
                this.cashierReady = true
                window.CashierInstanceReady = true
                await nextTick()
                this.customFunction && this.customFunction()
              },
              success: (data) => {
                console.log('success', data)
              }
            })
            api.css(this.composeCashierCss(customCss))
          }
        )
        window.CashierInstance = this.cashier
      } catch (err) {
        console.log(err)
        setTimeout(() => {
          this.loadCashierInstance()
        }, 200)
      }
    },
    nextTickMethod(config) {
      this.$nextTick(() => {
        const previewContainer = document.querySelector('#cashier-preview')
        previewContainer.style.height = config.containerWidth
        previewContainer.style.height = config.containerHeight
        if (this.setMaxHeight) {
          document.querySelector('#cashier-container').style.maxHeight =
            this.setMaxHeight
        }
      })
    },
    getConstantsAndCalculations() {
      let customCss = this.customCss && this.customCss[this.preset]
      let { config: stateConfigs, theme, containerSizeToNumber } = this
      let config = { ...stateConfigs }
      if (window.__PiqCashierConfigs__) {
        customCss = window.__PiqCashierConfigs__.customCss
        config = { ...window.__PiqCashierConfigs__.config }
        theme = { ...window.__PiqCashierConfigs__.theme }
      } else if (window.location.hash.includes('__PiqCashierConfigs__')) {
        const urlConfigsHash = JSON.parse(
          decodeURIComponent(window.location.hash).replace(
            '#__PiqCashierConfigs__=',
            ''
          )
        )
        customCss = urlConfigsHash.customCss
        config = { ...urlConfigsHash.config }
        theme = { ...urlConfigsHash.theme }
      }
      if (this.customConfigs) {
        for (const i in this.customConfigs) {
          config[i] = this.customConfigs[i]
        }
      }
      const containerWidthNumber = containerSizeToNumber(config.containerWidth)
      const containerHeightNumber = containerSizeToNumber(
        config.containerHeight
      )
      if (containerWidthNumber >= window.innerWidth - 25) {
        config.containerWidth = `${window.innerWidth - 25}px`
      }
      if (containerHeightNumber > window.innerHeight) {
        config.containerHeight = `${window.innerHeight - 35}px`
      }
      if (
        containerHeightNumber > window.innerHeight - 115 &&
        this.$route.path === '/showcase'
      ) {
        config.containerHeight = `${window.innerHeight - 140}px`
      }
      const loadingLayer = document.querySelector('#loadingLayer')
      if (loadingLayer) {
        loadingLayer.style.height = config.containerHeight
        loadingLayer.style.width = config.containerWidth
        if (config.containerHeight === 'auto') {
          // don't use fixed position when height is auto (allow scrolling when cashier overflows window height)
          loadingLayer.style.position = 'absolute'
        } else {
          loadingLayer.style.position = 'fixed'
        }
        if (
          config.lookupConfig &&
          Object.keys(config.lookupConfig).length > 0
        ) {
          // checkout requires a bit of padding - the container is fixed width, so the padding overflows, ugly fix
          if (window.innerWidth > 650) {
            loadingLayer.style.width = 'auto'
            loadingLayer.style.minWidth = config.containerWidth
            loadingLayer.style.maxWidth =
              parseInt(config.containerWidth) + 40 + 'px'
            loadingLayer.style.minHeight = '400px'
            document.getElementById('cashier-preview').style.padding = '0 20px'
          }
        } else {
          // reset to no minWidth or maxWidth
          loadingLayer.style.width = config.containerWidth
          loadingLayer.style.minWidth = 'auto'
          loadingLayer.style.maxWidth = 'none'
          loadingLayer.style.minHeight = 'auto'
        }
      }

      document.querySelector('#cashier-container').style.background =
        (theme.cashierbackground && theme.cashierbackground.color) || '#ffffff'
      return {
        customCss,
        config,
        theme
      }
    }
  },
  mounted() {
    this.loadCashierInstance()
  }
}
</script>

<style lang="scss" scoped>
#loadingLayer {
  box-shadow: 1px 1px 10px 10px rgba(0, 0, 0, 0.05) !important;
}
#cashier-container.fixedPosition {
  position: relative;
  position: fixed;
  &.showcase {
    overflow: hidden;
  }
}
#cashier-container {
  border-radius: 0.5rem !important;
}
</style>
